import React from 'react';
import axios from 'axios';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { Controller, type Path, type Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { ProgressBar } from '../../../components/ui/ProgressBar';
import { FormField } from '../../../components/forms/FormField';
import { SelectField } from '../../../components/forms/SelectField';
import { queryKeys } from '../../../hooks/useApi';
import { useTaskStatusQuery } from '../../../hooks/useTaskStatusQuery';
import { apiService } from '../../../services/api';
import type {
  ApiError,
  ComplianceDocumentDetail,
  ComplianceDocumentStatus,
} from '../../../types/api';
import { formatDateForInput } from '../../../utils/formatters';
import { parseTagsInput } from '../lib/filters';
import {
  COMPLIANCE_JURISDICTION_OPTIONS,
  COMPLIANCE_TYPE_DEFINITIONS,
  COMPLIANCE_TYPE_OPTIONS,
  EVENT_BASED_TYPES,
  getComplianceTypeDefinition,
} from '../lib/spec';
import {
  clearComplianceDraft,
  getComplianceDraft,
  setComplianceDraft,
} from '../lib/draft';
import {
  complianceRenewSchema,
  complianceSchema,
  complianceStatusOptions,
  type ComplianceFormValues,
} from '../lib/validation';
import { ComplianceFileField } from './ComplianceFileField';
import { computeFileSha256, validateComplianceFile } from '../lib/file';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const Section = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--text-primary);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-md);
`;

const HelperText = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
`;

const ErrorText = styled.p`
  margin: 0;
  color: var(--danger-600);
  font-size: var(--font-size-sm);
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: var(--space-xs);
  font-weight: 600;
  color: var(--text-primary);
`;

const TextArea = styled.textarea<{ $hasError: boolean }>`
  width: 100%;
  min-height: 96px;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  border: 1px solid
    ${(props) => (props.$hasError ? 'var(--danger-600)' : 'var(--border)')};
  font-size: 1rem;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--surface-strong);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--brand-600);
    box-shadow: 0 0 0 3px var(--brand-50);
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const HiddenDatalist = styled.datalist``;

const NOTES_MAX_LENGTH = 500;

const FIELD_NAMES = {
  type: 'type',
  issuing_authority: 'issuing_authority',
  jurisdiction: 'jurisdiction',
  issue_date: 'issue_date',
  expiry_date: 'expiry_date',
  next_due_date: 'next_due_date',
  status: 'status',
  notes: 'notes',
  tags: 'tags',
} satisfies Record<string, Path<ComplianceFormValues>>;

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

type ComplianceLogLevel = 'info' | 'warn' | 'error';

const logComplianceOcr = (
  level: ComplianceLogLevel,
  message: string,
  data?: Record<string, unknown>,
) => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }
  const payload = data ? { ...data } : undefined;
  const prefix = '[Compliance OCR]';
  if (level === 'error') {
    console.error(prefix, message, payload ?? '');
    return;
  }
  if (level === 'warn') {
    console.warn(prefix, message, payload ?? '');
    return;
  }
  console.info(prefix, message, payload ?? '');
};

interface ComplianceTypeMatch {
  key: string;
  score: number;
}

const matchComplianceTypeFromNotes = (notes: string) => {
  const normalizedNotes = normalizeText(notes);
  if (!normalizedNotes) return null;

  const tokens = new Set(
    normalizedNotes.split(' ').filter((token) => token.length > 2),
  );

  let bestMatch: ComplianceTypeMatch | null = null;

  for (const definition of COMPLIANCE_TYPE_DEFINITIONS) {
    const label = normalizeText(definition.label);
    const key = normalizeText(definition.key);
    let score = 0;

    if (label && normalizedNotes.includes(label)) {
      score += 5;
    }
    if (key && normalizedNotes.includes(key)) {
      score += 3;
    }

    label.split(' ').forEach((token) => {
      if (token.length < 3) return;
      if (tokens.has(token)) score += 1;
    });

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { key: definition.key, score };
    }
  }

  if (bestMatch === null || bestMatch.score < 2) {
    return null;
  }

  return bestMatch.key;
};

const buildStatusOptions = (statuses: ComplianceDocumentStatus[]) =>
  statuses.map((status) => ({
    value: status,
    label:
      status === 'draft'
        ? 'Borrador'
        : status === 'active'
          ? 'Activo'
          : status === 'expired'
            ? 'Vencido'
            : 'Archivado',
  }));

const parseApiErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object') {
    const detail = (error as ApiError & { detail?: unknown }).detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (
      detail &&
      typeof detail === 'object' &&
      'message' in detail &&
      typeof (detail as { message?: string }).message === 'string'
    ) {
      return (detail as { message: string }).message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
};

interface ComplianceFormProps {
  mode: 'create' | 'edit' | 'renew';
  document?: ComplianceDocumentDetail | null;
  onCancel: () => void;
  onSuccess: (documentId: string) => void;
}

interface ComplianceUploadSession {
  documentId: string;
  storageKey: string;
  checksum: string;
  originalFilename: string;
}

export const ComplianceForm: React.FC<ComplianceFormProps> = ({
  mode,
  document,
  onCancel,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const isEdit = mode === 'edit';
  const isRenew = mode === 'renew';
  const isCreate = mode === 'create';
  const location = useLocation();
  const prefillFromOcr = Boolean(
    (location.state as { prefillFromOcr?: boolean } | null)?.prefillFromOcr,
  );
  const extractedFields = document?.extracted_fields ?? null;
  const hasExtractedFields =
    extractedFields && Object.keys(extractedFields).length > 0;
  const requiresUploadOnRenewal = Boolean(isRenew);
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [draftFileName, setDraftFileName] = React.useState<string | null>(null);
  const [ocrPreviewFields, setOcrPreviewFields] =
    React.useState<ComplianceDocumentDetail['extracted_fields']>(null);
  const [ocrPreviewConfidence, setOcrPreviewConfidence] = React.useState<
    number | null
  >(null);
  const [ocrPreviewError, setOcrPreviewError] = React.useState<string | null>(
    null,
  );
  const [ocrPreviewTaskId, setOcrPreviewTaskId] = React.useState<string | null>(
    null,
  );
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadError, setUploadError] = React.useState<string | null>(null);
  const [uploadSession, setUploadSession] =
    React.useState<ComplianceUploadSession | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasAppliedOcr, setHasAppliedOcr] = React.useState(false);
  const uploadRequestRef = React.useRef(0);
  const ocrPreviewRequestRef = React.useRef(0);
  const ocrPreviewStatusRef = React.useRef<
    ReturnType<typeof useTaskStatusQuery>['normalizedStatus'] | null
  >(null);
  const lastAppliedPreviewTaskRef = React.useRef<string | null>(null);
  const notesReactId = React.useId();
  const notesInputId = `compliance-notes-${notesReactId}`;

  const defaultValues = React.useMemo<ComplianceFormValues>(
    () => ({
      type: document?.type ?? '',
      issuing_authority: document?.issuing_authority ?? '',
      jurisdiction: document?.jurisdiction ?? '',
      issue_date: document?.issue_date ?? formatDateForInput(new Date()),
      expiry_date: isRenew ? '' : (document?.expiry_date ?? ''),
      next_due_date: isRenew ? '' : (document?.next_due_date ?? ''),
      status: (isRenew
        ? 'active'
        : (document?.status ?? 'active')) as ComplianceDocumentStatus,
      notes: document?.notes ?? '',
      tags: document?.tags?.join(', ') ?? '',
    }),
    [document, isRenew],
  );

  const formSchema = isRenew ? complianceRenewSchema : complianceSchema;
  const statusValues: ComplianceDocumentStatus[] = isCreate
    ? ['draft', 'active']
    : complianceStatusOptions;
  const statusOptions = buildStatusOptions(statusValues);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    getValues,
    getFieldState,
    formState,
  } = useForm<ComplianceFormValues>({
    resolver: yupResolver(formSchema) as Resolver<ComplianceFormValues>,
    defaultValues,
  });
  const { errors } = formState;
  const draftKey = React.useMemo(() => {
    if (isCreate) {
      return 'create';
    }
    if (isRenew) {
      return `renew:${document?.id ?? 'unknown'}`;
    }
    return `edit:${document?.id ?? 'unknown'}`;
  }, [document?.id, isCreate, isRenew]);
  const hasHydratedDraftRef = React.useRef(false);

  const ocrTaskQuery = useTaskStatusQuery(ocrPreviewTaskId, {
    enabled: Boolean(ocrPreviewTaskId),
    refetchInterval: 5000,
    staleTime: 0,
  });
  const isOcrPreviewing =
    Boolean(ocrPreviewTaskId) &&
    (!ocrTaskQuery.normalizedStatus ||
      ['queued', 'processing', 'retrying'].includes(
        ocrTaskQuery.normalizedStatus,
      ));

  React.useEffect(() => {
    if (document) {
      reset(defaultValues);
    }
  }, [document, defaultValues, reset]);

  React.useEffect(() => {
    if (!isCreate || hasHydratedDraftRef.current) {
      return;
    }
    const draft = getComplianceDraft(draftKey);
    if (!draft) {
      hasHydratedDraftRef.current = true;
      return;
    }
    reset(draft.values);
    setDraftFileName(draft.fileName);
    setOcrPreviewTaskId(draft.ocrPreviewTaskId);
    hasHydratedDraftRef.current = true;
  }, [draftKey, isCreate, reset]);

  React.useEffect(() => {
    if (!isCreate) {
      return;
    }
    const subscription = watch((values) => {
      setComplianceDraft(draftKey, {
        values: values as ComplianceFormValues,
        fileName: draftFileName,
        ocrPreviewTaskId,
      });
    });
    return () => subscription.unsubscribe();
  }, [draftFileName, draftKey, isCreate, ocrPreviewTaskId, watch]);

  React.useEffect(() => {
    if (!isCreate) {
      return;
    }
    setComplianceDraft(draftKey, {
      values: getValues(),
      fileName: draftFileName,
      ocrPreviewTaskId,
    });
  }, [draftFileName, draftKey, getValues, isCreate, ocrPreviewTaskId]);

  const applyExtractedFields = React.useCallback(
    (
      fields: ComplianceDocumentDetail['extracted_fields'],
      options?: { onlyPristine?: boolean; toastMessage?: string | null },
    ) => {
      if (!fields || typeof fields !== 'object') {
        return;
      }

      const valueMap: Partial<ComplianceFormValues> = {};
      const payload = fields as Record<string, unknown>;

      if (typeof payload.issue_date === 'string') {
        valueMap.issue_date = payload.issue_date;
      }
      if (typeof payload.expiry_date === 'string') {
        valueMap.expiry_date = payload.expiry_date;
      }
      if (typeof payload.issuing_authority === 'string') {
        valueMap.issuing_authority = payload.issuing_authority;
      }
      if (typeof payload.jurisdiction === 'string') {
        valueMap.jurisdiction = payload.jurisdiction;
      }
      if (typeof payload.notes === 'string') {
        const trimmedNotes = payload.notes.slice(0, NOTES_MAX_LENGTH);
        valueMap.notes = trimmedNotes;
        if (isCreate) {
          const matchedType = matchComplianceTypeFromNotes(trimmedNotes);
          if (matchedType) {
            valueMap.type = matchedType;
          }
        }
      }
      if (Array.isArray(payload.tags)) {
        valueMap.tags = payload.tags.join(', ');
      }

      let appliedAny = false;
      (
        Object.entries(valueMap) as Array<
          [
            Path<ComplianceFormValues>,
            ComplianceFormValues[keyof ComplianceFormValues],
          ]
        >
      ).forEach(([key, value]) => {
        if (options?.onlyPristine && getFieldState(key, formState).isDirty) {
          return;
        }
        setValue(key, value, { shouldDirty: true });
        appliedAny = true;
      });

      if (!appliedAny) {
        return;
      }

      setHasAppliedOcr(true);
      const message =
        options?.toastMessage ?? 'Datos OCR aplicados al formulario.';
      if (message) {
        toast.success(message);
      }
    },
    [formState, getFieldState, isCreate, setValue],
  );

  React.useEffect(() => {
    if (
      !(isEdit || isRenew) ||
      !prefillFromOcr ||
      hasAppliedOcr ||
      !extractedFields
    ) {
      return;
    }
    applyExtractedFields(extractedFields);
  }, [
    applyExtractedFields,
    extractedFields,
    hasAppliedOcr,
    isEdit,
    isRenew,
    prefillFromOcr,
  ]);

  const selectedType = watch(FIELD_NAMES.type);
  const typeDefinition = React.useMemo(
    () => getComplianceTypeDefinition(selectedType || ''),
    [selectedType],
  );
  const requiresNextDueDate = Boolean(
    selectedType && EVENT_BASED_TYPES.has(selectedType),
  );

  const uploadFileToS3 = React.useCallback(
    async (
      presignedUrl: string,
      headers: Record<string, string>,
      targetFile: File,
    ) => {
      setUploadProgress(0);

      try {
        await axios.put(presignedUrl, targetFile, {
          headers,
          onUploadProgress: (event) => {
            if (!event.total) return;
            const nextProgress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(nextProgress);
          },
        });
      } catch (error) {
        if (
          typeof axios.isAxiosError === 'function' &&
          axios.isAxiosError(error)
        ) {
          if (!error.response) {
            throw new Error(
              'No se pudo subir el archivo. Verifica tu conexión o la configuración de CORS del bucket.',
            );
          }
        }
        throw error;
      }
    },
    [],
  );

  const createUploadSession = React.useCallback(
    async (targetFile: File): Promise<ComplianceUploadSession> => {
      setIsUploading(true);
      setUploadProgress(0);

      try {
        const initResult = await apiService.initComplianceUpload({
          original_filename: targetFile.name,
          mime_type: targetFile.type,
          size_bytes: targetFile.size,
        });

        const checksum = await computeFileSha256(targetFile);
        await uploadFileToS3(
          initResult.presigned_put_url,
          initResult.presigned_put_headers,
          targetFile,
        );

        return {
          documentId: initResult.document_id,
          storageKey: initResult.storage_key,
          checksum,
          originalFilename: targetFile.name,
        };
      } finally {
        setIsUploading(false);
      }
    },
    [uploadFileToS3],
  );

  const handleFileChange = React.useCallback((nextFile: File | null) => {
    if (!nextFile) {
      logComplianceOcr('info', 'File cleared');
      setFile(null);
      setFileError(null);
      setUploadError(null);
      setUploadProgress(0);
      setUploadSession(null);
      setDraftFileName(null);
      setOcrPreviewFields(null);
      setOcrPreviewConfidence(null);
      setOcrPreviewError(null);
      setOcrPreviewTaskId(null);
      ocrPreviewStatusRef.current = null;
      lastAppliedPreviewTaskRef.current = null;
      return;
    }

    const validationMessage = validateComplianceFile(nextFile);
    if (validationMessage) {
      logComplianceOcr('warn', 'File validation failed', {
        fileName: nextFile.name,
        fileSize: nextFile.size,
        fileType: nextFile.type,
        message: validationMessage,
      });
      setFile(null);
      setFileError(validationMessage);
      setUploadSession(null);
      setDraftFileName(null);
      setOcrPreviewFields(null);
      setOcrPreviewConfidence(null);
      setOcrPreviewError(null);
      setOcrPreviewTaskId(null);
      ocrPreviewStatusRef.current = null;
      lastAppliedPreviewTaskRef.current = null;
      return;
    }

    logComplianceOcr('info', 'File selected', {
      fileName: nextFile.name,
      fileSize: nextFile.size,
      fileType: nextFile.type,
    });
    setFile(nextFile);
    setFileError(null);
    setUploadError(null);
    setUploadSession(null);
    setDraftFileName(nextFile.name);
    setOcrPreviewFields(null);
    setOcrPreviewConfidence(null);
    setOcrPreviewError(null);
    setOcrPreviewTaskId(null);
    ocrPreviewStatusRef.current = null;
    lastAppliedPreviewTaskRef.current = null;
  }, []);

  React.useEffect(() => {
    if (isEdit) {
      setOcrPreviewError(null);
      setOcrPreviewFields(null);
      setOcrPreviewConfidence(null);
      setOcrPreviewTaskId(null);
      setUploadSession(null);
      ocrPreviewStatusRef.current = null;
      lastAppliedPreviewTaskRef.current = null;
      return;
    }

    if (!file) {
      setOcrPreviewError(null);
      setOcrPreviewFields(null);
      setOcrPreviewConfidence(null);
      ocrPreviewStatusRef.current = null;
      lastAppliedPreviewTaskRef.current = null;
      return;
    }

    if (uploadSession) {
      return;
    }

    let isActive = true;
    const requestId = ++uploadRequestRef.current;

    const startUploadFlow = async () => {
      setUploadError(null);

      let session: ComplianceUploadSession;
      try {
        session = await createUploadSession(file);
      } catch (error) {
        if (!isActive || requestId !== uploadRequestRef.current) {
          return;
        }
        setUploadError(
          parseApiErrorMessage(error, 'No se pudo subir el archivo.'),
        );
        return;
      }

      if (!isActive || requestId !== uploadRequestRef.current) {
        return;
      }

      setUploadSession(session);
    };

    void startUploadFlow();

    return () => {
      isActive = false;
    };
  }, [createUploadSession, file, isEdit, uploadSession]);

  React.useEffect(() => {
    if (isEdit || !file) {
      logComplianceOcr('info', 'OCR preview skipped', {
        reason: isEdit ? 'edit_mode' : 'no_file',
      });
      return;
    }

    let isActive = true;
    const requestId = ++ocrPreviewRequestRef.current;

    const startPreview = async () => {
      setOcrPreviewError(null);
      logComplianceOcr('info', 'OCR preview starting', {
        requestId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });

      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const response = await apiService.startComplianceOcr(formData);

        if (!isActive || requestId !== ocrPreviewRequestRef.current) {
          logComplianceOcr('warn', 'OCR preview response ignored', {
            requestId,
          });
          return;
        }
        logComplianceOcr('info', 'OCR preview task created', {
          requestId,
          taskId: response.taskId,
        });
        setOcrPreviewTaskId(response.taskId);
        ocrPreviewStatusRef.current = null;
      } catch (error) {
        if (!isActive || requestId !== ocrPreviewRequestRef.current) {
          return;
        }
        const errorMessage = parseApiErrorMessage(
          error,
          'No se pudo iniciar el OCR.',
        );
        logComplianceOcr('error', 'OCR preview request failed', {
          requestId,
          message: errorMessage,
        });
        setOcrPreviewError(errorMessage);
        setOcrPreviewTaskId(null);
      }
    };

    void startPreview();

    return () => {
      isActive = false;
    };
  }, [file, isEdit]);

  React.useEffect(() => {
    if (!ocrPreviewTaskId) {
      return;
    }

    const status = ocrTaskQuery.normalizedStatus;
    if (!status || status === ocrPreviewStatusRef.current) {
      return;
    }
    ocrPreviewStatusRef.current = status;
    logComplianceOcr('info', 'OCR status updated', {
      taskId: ocrPreviewTaskId,
      status,
    });

    if (status === 'success') {
      if (lastAppliedPreviewTaskRef.current === ocrPreviewTaskId) {
        return;
      }
      const details = ocrTaskQuery.data?.details;
      if (details && typeof details === 'object' && !Array.isArray(details)) {
        const extractedFields = (details as Record<string, unknown>)
          .extracted_fields;
        const ocrConfidence = (details as Record<string, unknown>)
          .ocr_confidence;
        if (extractedFields && typeof extractedFields === 'object') {
          logComplianceOcr('info', 'OCR fields received', {
            taskId: ocrPreviewTaskId,
            fieldCount: Object.keys(extractedFields).length,
            confidence: ocrConfidence ?? null,
          });
          setOcrPreviewFields(extractedFields as Record<string, unknown>);
          if (typeof ocrConfidence === 'number') {
            setOcrPreviewConfidence(ocrConfidence);
          } else {
            setOcrPreviewConfidence(null);
          }
          applyExtractedFields(extractedFields as Record<string, unknown>, {
            onlyPristine: true,
            toastMessage: 'Datos OCR aplicados automáticamente.',
          });
          lastAppliedPreviewTaskRef.current = ocrPreviewTaskId;
          setOcrPreviewError(null);
          setOcrPreviewTaskId(null);
        }
      }
      return;
    }

    if (status === 'failure' || status === 'canceled') {
      const errorMessage =
        ocrTaskQuery.data?.error ?? 'No se pudo extraer datos OCR.';
      logComplianceOcr('error', 'OCR task failed', {
        taskId: ocrPreviewTaskId,
        status,
        message: errorMessage,
      });
      setOcrPreviewError(errorMessage);
      setOcrPreviewTaskId(null);
    }
  }, [
    applyExtractedFields,
    ocrPreviewTaskId,
    ocrTaskQuery.data,
    ocrTaskQuery.normalizedStatus,
  ]);

  const onSubmit = async (values: ComplianceFormValues) => {
    setUploadError(null);

    if (isUploading && !uploadSession) {
      setUploadError('Espera a que termine la carga del archivo.');
      return;
    }

    if ((isCreate || requiresUploadOnRenewal) && !file) {
      setFileError(
        requiresUploadOnRenewal
          ? 'Adjunta un archivo para renovar este documento.'
          : 'Adjunta un archivo antes de guardar.',
      );
      return;
    }

    if (file) {
      const validationMessage = validateComplianceFile(file);
      if (validationMessage) {
        setFileError(validationMessage);
        return;
      }
    }

    try {
      setIsSaving(true);

      const tags = parseTagsInput(values.tags ?? '');
      const statusValue = isRenew ? 'active' : values.status;
      const payloadBase = {
        type: values.type,
        issuing_authority: values.issuing_authority.trim(),
        jurisdiction: values.jurisdiction.trim(),
        issue_date: values.issue_date,
        ...(values.expiry_date ? { expiry_date: values.expiry_date } : {}),
        ...(values.next_due_date
          ? { next_due_date: values.next_due_date }
          : {}),
        status: statusValue,
        ...(values.notes?.trim()
          ? { notes: values.notes.trim() }
          : { notes: '' }),
        ...(tags.length > 0 ? { tags } : { tags: [] }),
      };
      const ocrPreviewPayload =
        ocrPreviewFields !== null
          ? {
              extracted_fields: ocrPreviewFields,
              ...(ocrPreviewConfidence !== null
                ? { ocr_confidence: ocrPreviewConfidence }
                : {}),
            }
          : {};
      let resolvedUploadSession = uploadSession;

      if ((isCreate || isRenew) && file && !resolvedUploadSession) {
        try {
          resolvedUploadSession = await createUploadSession(file);
          setUploadSession(resolvedUploadSession);
        } catch (error) {
          setUploadError(
            parseApiErrorMessage(error, 'No se pudo subir el archivo.'),
          );
          return;
        }
      }

      if (isEdit) {
        if (!document) {
          throw new Error('Documento no encontrado.');
        }

        await apiService.updateComplianceDocument(document.id, payloadBase);
        queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
        queryClient.invalidateQueries({
          queryKey: queryKeys.compliance.detail(document.id),
        });
        toast.success('Documento actualizado.');
        onSuccess(document.id);
        return;
      }

      if (isRenew) {
        if (!document) {
          throw new Error('Documento no encontrado.');
        }
        if (!file) {
          throw new Error('Adjunta un archivo para renovar este documento.');
        }
        if (!resolvedUploadSession) {
          throw new Error('No se pudo preparar el archivo para renovar.');
        }

        const created = await apiService.finalizeComplianceDocument({
          ...payloadBase,
          ...ocrPreviewPayload,
          document_id: resolvedUploadSession.documentId,
          storage_key: resolvedUploadSession.storageKey,
          original_filename: resolvedUploadSession.originalFilename,
          checksum: resolvedUploadSession.checksum,
          supersedes_document_id: document.id,
        });

        queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
        queryClient.invalidateQueries({
          queryKey: queryKeys.compliance.detail(created.id),
        });
        toast.success('Documento renovado correctamente.');
        onSuccess(created.id);
        return;
      }

      if (!file) {
        throw new Error('Adjunta un archivo antes de guardar.');
      }
      if (!resolvedUploadSession) {
        throw new Error('No se pudo preparar el archivo para guardar.');
      }

      const created = await apiService.finalizeComplianceDocument({
        ...payloadBase,
        ...ocrPreviewPayload,
        document_id: resolvedUploadSession.documentId,
        storage_key: resolvedUploadSession.storageKey,
        original_filename: resolvedUploadSession.originalFilename,
        checksum: resolvedUploadSession.checksum,
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.compliance.detail(created.id),
      });
      toast.success('Documento creado correctamente.');
      if (isCreate) {
        clearComplianceDraft(draftKey);
      }
      onSuccess(created.id);
    } catch (error) {
      const message = parseApiErrorMessage(
        error,
        'No se pudo guardar el documento.',
      );
      if (isUploading) {
        setUploadError(message);
      } else {
        toast.error(message);
      }
    } finally {
      setIsUploading(false);
      setIsSaving(false);
    }
  };

  const expiryLabel = isRenew
    ? 'Nueva fecha de vencimiento'
    : 'Fecha de vencimiento (opcional)';
  const expiryHelperText = isRenew
    ? 'Ingresa la nueva fecha de vencimiento para renovar.'
    : 'Si se deja vacío, se calculará automáticamente.';
  const fileHelperText = isRenew
    ? 'Archivo requerido para renovar. PDF, JPG o PNG. Máximo 5MB.'
    : 'PDF, JPG o PNG. Máximo 5MB.';
  const notesErrorId = errors.notes ? `${notesInputId}-error` : undefined;

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <Section>
        <SectionTitle>Información principal</SectionTitle>
        <Grid>
          <Controller
            name={FIELD_NAMES.type}
            control={control}
            render={({ field, fieldState }) => (
              <SelectField
                label="Tipo de documento"
                name={FIELD_NAMES.type}
                options={COMPLIANCE_TYPE_OPTIONS}
                placeholder="Selecciona un tipo"
                value={field.value}
                onChange={(value) => field.onChange(value ?? '')}
                error={fieldState.error}
                required
                disabled={isRenew}
              />
            )}
          />
          <FormField
            label="Autoridad emisora"
            name={FIELD_NAMES.issuing_authority}
            placeholder="Ej: Agencia Ambiental"
            required
            register={register}
            error={errors.issuing_authority}
          />
          <FormField
            label="Jurisdicción"
            name={FIELD_NAMES.jurisdiction}
            placeholder="Ej: CABA"
            required
            register={register}
            error={errors.jurisdiction}
            list="compliance-jurisdictions"
          />
          <FormField
            label="Fecha de emisión"
            name={FIELD_NAMES.issue_date}
            type="date"
            required
            register={register}
            error={errors.issue_date}
          />
          <FormField
            label={expiryLabel}
            name={FIELD_NAMES.expiry_date}
            type="date"
            register={register}
            error={errors.expiry_date}
            helperText={expiryHelperText}
            required={isRenew}
          />
          {requiresNextDueDate && (
            <FormField
              label="Próxima fecha de vencimiento"
              name={FIELD_NAMES.next_due_date}
              type="date"
              required
              register={register}
              error={errors.next_due_date}
              helperText="Requerido para documentos por evento."
            />
          )}
          {!isRenew && (
            <Controller
              name={FIELD_NAMES.status}
              control={control}
              render={({ field, fieldState }) => (
                <SelectField
                  label="Estado"
                  name={FIELD_NAMES.status}
                  options={statusOptions}
                  value={field.value}
                  onChange={(value) =>
                    field.onChange(
                      (value ?? 'draft') as ComplianceDocumentStatus,
                    )
                  }
                  error={fieldState.error}
                  required
                />
              )}
            />
          )}
        </Grid>
        {typeDefinition && (
          <HelperText>
            Evidencia requerida: {typeDefinition.evidence}
            {typeDefinition.appliesToAll
              ? ' · Aplica a todas las jurisdicciones.'
              : typeDefinition.jurisdictions.length > 0
                ? ` · Jurisdicciones: ${typeDefinition.jurisdictions.join(', ')}.`
                : ''}
            {typeDefinition.jurisdictionNotes
              ? ` · ${typeDefinition.jurisdictionNotes}.`
              : ''}
          </HelperText>
        )}
        {(isEdit || isRenew) && hasExtractedFields && (
          <div>
            <HelperText>
              Hay datos OCR disponibles para este documento.
            </HelperText>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => applyExtractedFields(extractedFields)}
              style={{ marginTop: 'var(--space-sm)' }}
            >
              Aplicar datos OCR
            </Button>
          </div>
        )}
      </Section>

      <Section>
        <SectionTitle>Etiquetas y notas</SectionTitle>
        <Grid>
          <FormField
            label="Etiquetas"
            name={FIELD_NAMES.tags}
            placeholder="Ej: habilitacion, higiene"
            register={register}
            error={errors.tags}
            helperText="Separadas por coma."
          />
          <Controller
            name={FIELD_NAMES.notes}
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <FieldLabel htmlFor={notesInputId}>Notas</FieldLabel>
                <TextArea
                  {...field}
                  id={notesInputId}
                  $hasError={Boolean(fieldState.error)}
                  placeholder="Notas adicionales sobre el documento"
                  maxLength={NOTES_MAX_LENGTH}
                  aria-invalid={Boolean(fieldState.error) || undefined}
                  aria-describedby={notesErrorId}
                />
                {fieldState.error && (
                  <ErrorText id={notesErrorId} role="alert">
                    {fieldState.error.message}
                  </ErrorText>
                )}
              </div>
            )}
          />
        </Grid>
      </Section>

      <Section>
        <SectionTitle>Archivo</SectionTitle>
        {isEdit ? (
          <HelperText>
            Para actualizar el archivo, utiliza la renovación del documento.
          </HelperText>
        ) : (
          <>
            <ComplianceFileField
              file={file}
              onFileChange={handleFileChange}
              error={fileError}
              disabled={isUploading || isSaving}
              helperText={fileHelperText}
              required={isCreate || requiresUploadOnRenewal}
            />
            {isOcrPreviewing && (
              <HelperText role="status" aria-live="polite">
                Extrayendo datos con OCR...
              </HelperText>
            )}
            {ocrPreviewError && (
              <ErrorText role="alert">{ocrPreviewError}</ErrorText>
            )}
            {isUploading && (
              <ProgressBar
                progress={uploadProgress}
                text="Subiendo archivo..."
                size="sm"
                animated
              />
            )}
            {uploadError && <ErrorText role="alert">{uploadError}</ErrorText>}
          </>
        )}
      </Section>

      <HiddenDatalist id="compliance-jurisdictions">
        {COMPLIANCE_JURISDICTION_OPTIONS.map((value) => (
          <option key={value} value={value} />
        ))}
      </HiddenDatalist>

      <ActionsRow>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            if (isCreate) {
              clearComplianceDraft(draftKey);
            }
            onCancel();
          }}
          disabled={isUploading || isSaving}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isUploading || isSaving}
        >
          {isRenew
            ? 'Renovar documento'
            : isEdit
              ? 'Guardar cambios'
              : 'Crear documento'}
        </Button>
      </ActionsRow>
    </FormContainer>
  );
};

ComplianceForm.displayName = 'ComplianceForm';
